import SimulationResumeChoice from '../components/SimulationResumeChoice';
import React from 'react';
import {
  Play,
  Pause,
  Volume2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  X,
  ArrowUp,
  ArrowDown,
  AlertCircle,
} from 'lucide-react';
import { SimulationController } from '../hooks/useSimulationController';

interface Props {
  controller: SimulationController;
}

const SimulationTestMobile: React.FC<Props> = ({ controller }) => {
  const {
    navigate,
    currentConfig,
    UNKNOWN_ANSWER,
    loading,
    initializingApi,
    error,
    showResults,
    scoreSummary,
    currentTest,
    isPlaying,
    audioElement,
    audioPlayedMap,
    groupSelections,
    writingAnswers,
    isSubmitting,
    isCheckpointSaving,
    isManualSaving,
    checkpointError,
    saveProgressMessage,
    saveProgressComplete,
    isListeningPart,
    isReadingPart,
    isSequenceQuestion,
    isTextEntryQuestion,
    isLongFormWritingQuestion,
    isVerticalSequenceQuestion,
    isImagePath,
    resolveQuestionImagePath,
    stripImagePathFromText,
    toMediaUrl,
    getTokens,
    getTokenCounts,
    renderText,
    handleAnswer,
    handleNext,
    handleManualSave,
    previousQuestion,
    playAudio,
    selectToken,
    removeToken,
    moveToken,
    onInputChange,
  } = controller;
  void isReadingPart;

  // ---- Result screen ----
  if (showResults && scoreSummary) {
    const cards = [
      { key: 'listening', label: 'Listening', value: scoreSummary.listening, bg: 'bg-blue-50', text: 'text-blue-700' },
      { key: 'reading', label: 'Reading', value: scoreSummary.reading, bg: 'bg-emerald-50', text: 'text-emerald-700' },
      ...(currentConfig.hasWriting ? [{ key: 'writing', label: 'Writing', value: scoreSummary.writing, bg: 'bg-purple-50', text: 'text-purple-700' }] : []),
    ];
    return (
      <div className="pb-6">
        <div className="bg-white rounded-2xl p-5 border border-gray-100 space-y-5">
          <div className="text-center">
            <h1 className="text-xl font-bold text-[#312E81] mb-2">{renderText(`${currentConfig.title}成绩`)}</h1>
            <div className="text-4xl font-extrabold text-[#4F46E5]">{scoreSummary.total.toFixed(1)}</div>
            <div className="text-sm text-gray-400">/ {scoreSummary.max}</div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {cards.map((c) => (
              <div key={c.key} className={`${c.bg} p-3 rounded-xl text-center`}>
                <div className="text-xs text-gray-600 mb-0.5">{c.label}</div>
                <div className={`text-xl font-bold ${c.text}`}>{c.value.toFixed(1)}</div>
              </div>
            ))}
          </div>

          <div className="text-center text-xs text-gray-400">{renderText('详细回顾请到 History 页面查看')}</div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/')}
              className="py-3 border border-gray-200 rounded-xl font-bold text-gray-600 active:bg-gray-50"
            >
              {renderText('返回首页')}
            </button>
            <button
              onClick={() => navigate('/history')}
              className="py-3 bg-[#4F46E5] text-white rounded-xl font-bold active:bg-indigo-700"
            >
              {renderText('查看 History')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ---- Loading / error ----
  if (controller.resumeChoices) return <SimulationResumeChoice records={controller.resumeChoices} onResume={controller.chooseResume} onNew={controller.chooseNewSimulation} onCancel={() => navigate('/')} />;

  if (loading || initializingApi) {
    return (
      <div className="pt-20 flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full border-2 border-indigo-200 border-t-[#4F46E5] animate-spin"></div>
        <span className="text-sm text-gray-500">Loading Simulation...</span>
      </div>
    );
  }
  if (error && !controller.sessionReady) {
    return (
      <div className="pt-10 text-center">
        <div className="bg-white p-5 rounded-2xl border border-red-100">
          <div className="w-12 h-12 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="w-6 h-6" />
          </div>
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!currentTest) return <div className="pt-10 text-center text-gray-500">No test loaded</div>;

  const currentQuestion = currentTest.questions[currentTest.currentQuestionIndex];
  const progress = ((currentTest.currentQuestionIndex + 1) / currentTest.questions.length) * 100;
  const questionImagePath = resolveQuestionImagePath(currentQuestion);
  const questionText = stripImagePathFromText(currentQuestion.question);
  const selectedAnswer = currentTest.answers[currentTest.currentQuestionIndex];
  const audioPlayed = audioElement ? audioPlayedMap[audioElement.src] : false;

  // ---- Choice options (listening / reading) ----
  const renderChoiceOptions = () => {
    const hasImageOptions = currentQuestion.options?.some(isImagePath) ?? false;
    const containerClass = hasImageOptions ? 'grid grid-cols-2 gap-3' : 'space-y-2.5';

    return (
      <div className={containerClass}>
        {currentQuestion.options.map((option, index) => {
          const isUsedInGroup = groupSelections.includes(index);
          const isSelected = selectedAnswer === index;
          const isImage = isImagePath(option);
          const label = String.fromCharCode(65 + index);

          return (
            <button
              key={index}
              onClick={() => handleAnswer(index)}
              className={`relative text-left rounded-2xl border-2 transition-all active:scale-[0.99] ${
                hasImageOptions ? 'p-3 flex flex-col items-center' : 'p-3.5 flex items-center gap-3'
              } ${
                isSelected
                  ? 'border-[#4F46E5] bg-indigo-50'
                  : isUsedInGroup
                    ? 'border-gray-100 bg-gray-50 opacity-60'
                    : 'border-gray-100 bg-white'
              }`}
            >
              {isSelected && (
                <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-[#4F46E5] fill-current" />
              )}
              {isUsedInGroup && (
                <span className="absolute top-1.5 right-1.5 bg-gray-200 text-gray-500 text-[10px] px-1.5 py-0.5 rounded-full font-bold">
                  已选
                </span>
              )}
              <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center font-bold text-base ${
                isSelected ? 'bg-[#4F46E5] text-white' : 'bg-gray-100 text-gray-400'
              } ${hasImageOptions ? 'mb-2' : ''}`}>
                {label}
              </div>
              {isImage ? (
                <img
                  src={toMediaUrl(option)}
                  alt={`Option ${label}`}
                  className={`w-full h-28 object-contain rounded-lg ${isUsedInGroup ? 'grayscale' : ''}`}
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.opacity = '0.3';
                  }}
                />
              ) : (
                <span className={`flex-1 text-base ${
                  isUsedInGroup ? 'line-through text-gray-400' : 'text-gray-800 font-medium'
                }`}>
                  {renderText(option)}
                </span>
              )}
            </button>
          );
        })}

        <button
          onClick={() => handleAnswer(UNKNOWN_ANSWER)}
          className={`relative text-left rounded-2xl border-2 transition-all active:scale-[0.99] ${
            hasImageOptions ? 'p-3 flex flex-col items-center justify-center' : 'p-3.5 flex items-center gap-3'
          } ${
            selectedAnswer === UNKNOWN_ANSWER
              ? 'border-orange-400 bg-orange-50'
              : 'border-gray-100 bg-white'
          }`}
        >
          <div className={`shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${
            selectedAnswer === UNKNOWN_ANSWER ? 'bg-orange-500 text-white' : 'bg-orange-100 text-orange-400'
          } ${hasImageOptions ? 'mb-2' : ''}`}>
            <HelpCircle className="w-5 h-5" />
          </div>
          <span className={`text-base font-medium ${
            selectedAnswer === UNKNOWN_ANSWER ? 'text-orange-700' : 'text-gray-500'
          }`}>
            {renderText('不知道')}
          </span>
        </button>
      </div>
    );
  };

  // ---- Sequence composer (tap to add, arrow buttons to reorder, no HTML5 drag) ----
  const renderSequenceComposer = () => {
    const wa = writingAnswers[currentTest.currentQuestionIndex] || {};
    const tokens = getTokens(currentQuestion);
    const usedTokens = wa.sequence || [];
    const vertical = isVerticalSequenceQuestion(currentQuestion);

    const totalCounts = getTokenCounts(tokens);
    const usedCounts = getTokenCounts(usedTokens);
    const remaining: string[] = [];
    Object.keys(totalCounts).forEach((t) => {
      for (let i = 0; i < (totalCounts[t] - (usedCounts[t] || 0)); i++) remaining.push(t);
    });

    return (
      <div>
        <p className="text-xs text-gray-500 mb-3">
          {vertical ? '点击下方句子加入答案，使用 ▲▼ 调整顺序' : '点击下方词语加入答案，使用 ▲▼ 调整顺序'}
        </p>

        {/* Answer area */}
        <div className={`p-3 rounded-2xl bg-indigo-50/60 border-2 border-dashed border-indigo-200 min-h-20 mb-4 ${
          vertical ? 'space-y-2' : 'flex flex-wrap gap-2'
        }`}>
          {usedTokens.length === 0 && (
            <span className="text-gray-400 italic text-sm">
              {vertical ? '点击下方句子作答...' : '点击下方词语作答...'}
            </span>
          )}
          {usedTokens.map((t, i) => (
            <div
              key={`${t}-${i}`}
              className={`bg-[#4F46E5] text-white rounded-xl flex items-center ${
                vertical ? 'w-full px-3 py-2.5 justify-between' : 'px-3 py-2 gap-2'
              }`}
            >
              <span className="flex-1 text-base">{renderText(t)}</span>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => moveToken(i, -1)}
                  disabled={i === 0}
                  aria-label="Move up"
                  className="w-7 h-7 rounded-lg bg-white/15 active:bg-white/30 disabled:opacity-30 flex items-center justify-center"
                >
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button
                  onClick={() => moveToken(i, 1)}
                  disabled={i === usedTokens.length - 1}
                  aria-label="Move down"
                  className="w-7 h-7 rounded-lg bg-white/15 active:bg-white/30 disabled:opacity-30 flex items-center justify-center"
                >
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button
                  onClick={() => removeToken(i)}
                  aria-label="Remove"
                  className="w-7 h-7 rounded-lg bg-white/15 active:bg-white/30 flex items-center justify-center"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Token pool */}
        <div className={vertical ? 'space-y-2' : 'flex flex-wrap gap-2'}>
          {remaining.map((t, i) => (
            <button
              key={`${t}-${i}`}
              onClick={() => selectToken(t)}
              className={`bg-white border border-gray-200 active:bg-indigo-50 active:border-indigo-300 ${
                vertical
                  ? 'block w-full text-left rounded-xl px-3 py-3 text-base'
                  : 'px-3 py-2 rounded-lg text-base'
              }`}
            >
              {renderText(t)}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isSequenceQuestion(currentQuestion)) return renderSequenceComposer();
    if (isListeningPart(currentQuestion) || isReadingPart(currentQuestion)) return renderChoiceOptions();
    if (isTextEntryQuestion(currentQuestion)) {
      const wa = writingAnswers[currentTest.currentQuestionIndex] || {};
      const isLong = isLongFormWritingQuestion(currentQuestion);
      return (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            {isLong ? '请根据题目和图片造句：' : '请输入答案：'}
          </label>
          {isLong ? (
            <textarea
              className="w-full border border-gray-200 bg-gray-50 p-3 rounded-2xl min-h-32 resize-y text-base focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent focus:bg-white"
              value={wa.text || ''}
              onChange={onInputChange}
              placeholder="根据题目和图片写一句完整的句子..."
            />
          ) : (
            <input
              type="text"
              className="w-full border border-gray-200 bg-gray-50 px-4 py-3 rounded-2xl text-base focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:border-transparent focus:bg-white"
              value={wa.text || ''}
              onChange={onInputChange}
              placeholder="在此输入..."
            />
          )}
        </div>
      );
    }
    return <div className="text-gray-400 text-sm">Unknown Part</div>;
  };

  const isLast = currentTest.currentQuestionIndex === currentTest.questions.length - 1;

  return (
    <div
      className="pb-32"
      style={{ paddingBottom: 'calc(8rem + env(safe-area-inset-bottom, 0px))' }}
    >
      {/* Sticky header with progress */}
      <div className="sticky top-0 -mx-4 px-4 pt-3 pb-2 bg-[#EEF2FF]/95 backdrop-blur-md z-30">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-bold text-[#4F46E5] uppercase tracking-wider">{currentConfig.description}</div>
            <div className="text-sm font-bold text-[#312E81]">
              {renderText(`第 ${currentTest.currentQuestionIndex + 1} / ${currentTest.questions.length} 题`)}
            </div>
          </div>
          <div className="text-xs font-bold text-gray-500">{Math.round(progress)}%</div>
        </div>
        <div className="h-2 bg-white rounded-full overflow-hidden">
          <div className="h-full bg-[#22C55E] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="mt-4 space-y-4">
        {error && (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {renderText(error)}
          </div>
        )}
        {checkpointError && (
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {renderText(checkpointError)}
          </div>
        )}

        {saveProgressMessage && (
          <div role="status" className={`rounded-2xl border px-4 py-3 text-sm ${saveProgressComplete ? 'border-green-200 bg-green-50 text-green-700' : 'border-amber-200 bg-amber-50 text-amber-800'}`}>
            {renderText(saveProgressMessage)}
          </div>
        )}

        {/* Audio card */}
        {isListeningPart(currentQuestion) && currentQuestion.media && (
          <div className="bg-[#4F46E5] rounded-2xl p-4 flex items-center gap-3 text-white shadow-lg shadow-indigo-500/20">
            <button
              onClick={playAudio}
              disabled={audioPlayed}
              className={`w-14 h-14 rounded-full flex items-center justify-center shrink-0 ${
                isPlaying
                  ? 'bg-white text-[#4F46E5]'
                  : audioPlayed
                    ? 'bg-indigo-400 text-indigo-200'
                    : 'bg-white/20 active:bg-white/30 text-white'
              }`}
            >
              {isPlaying ? (
                <Pause className="w-6 h-6 fill-current" />
              ) : audioPlayed ? (
                <CheckCircle className="w-6 h-6" />
              ) : (
                <Play className="w-6 h-6 fill-current ml-0.5" />
              )}
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base">
                {audioPlayed ? '已播放' : isPlaying ? '播放中...' : '播放音频'}
              </div>
              <div className="text-indigo-200 text-xs">
                {audioPlayed ? '只能播放一次' : '请仔细聆听'}
              </div>
            </div>
            <Volume2 className="w-6 h-6 text-indigo-300 opacity-60" />
          </div>
        )}

        {/* Question image */}
        {questionImagePath && (
          <div className="rounded-2xl bg-white border border-gray-100 p-3">
            <img src={toMediaUrl(questionImagePath)} className="max-h-56 w-full object-contain mx-auto" />
          </div>
        )}

        {/* Question text */}
        {!isSequenceQuestion(currentQuestion) && questionText && (
          <div className="text-lg font-medium text-[#312E81] whitespace-pre-line leading-relaxed px-1">
            {renderText(questionText.replace(/★/g, '\n★'))}
          </div>
        )}

        {/* Content area */}
        <div>{renderContent()}</div>

        {checkpointError && (
          <div className="p-3 bg-red-50 text-red-600 rounded-xl border border-red-200 text-sm">
            {renderText(checkpointError)}
          </div>
        )}
      </div>

      {/* Sticky bottom action bar */}
      <div
        className="fixed left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-30"
        style={{
          bottom: 'calc(56px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="flex items-center gap-3">
          <button
            onClick={previousQuestion}
            disabled={currentTest.currentQuestionIndex === 0 || isSubmitting || isCheckpointSaving || isManualSaving}
            className="w-14 h-12 flex items-center justify-center rounded-xl border border-gray-200 text-gray-500 active:bg-gray-50 disabled:opacity-30"
            aria-label="Previous question"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={handleManualSave}
            disabled={isSubmitting || isCheckpointSaving || isManualSaving}
            className="px-4 h-12 rounded-xl border border-emerald-300 text-emerald-700 bg-white text-sm font-bold active:bg-emerald-50 disabled:opacity-30"
          >
            {isManualSaving ? '保存中...' : '保存'}
          </button>
          <button
            onClick={handleNext}
            disabled={isSubmitting || isCheckpointSaving || isManualSaving}
            className={`flex-1 h-12 rounded-xl font-bold text-white text-base flex items-center justify-center gap-2 active:scale-[0.99] transition-transform ${
              isLast ? 'bg-[#22C55E] active:bg-green-600' : 'bg-[#4F46E5] active:bg-indigo-700'
            } disabled:opacity-50`}
          >
            {isSubmitting
              ? '提交中...'
              : isCheckpointSaving || isManualSaving
                ? '保存中...'
                : isLast
                  ? '提交试卷'
                  : (
                    <>
                      下一题
                      <ChevronRight className="w-5 h-5" />
                    </>
                  )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimulationTestMobile;
